/**
 * The Mail pages' ONE client module (NT-14): the three reads and the four writes of the console's
 * postal mail exception. The Worker side is `src/worker/mail-writes.ts` and `catalog-mail.ts`.
 *
 * The writes are same-origin `fetch` with `credentials: "same-origin"` — the deciding credential
 * is the operator's HttpOnly session cookie, never a bearer — and carry the custom
 * `X-Admin-Mail-Write: 1` header, a JSON body, or multipart for the letter upload.
 *
 * What decides what staff see (the purpose words, where the subject lives, the USPS link, the
 * review step's checks) is a pure function here, tested without a DOM; the screens only draw it.
 */
import {
  adminPost,
  BASE_FAILURE_MESSAGES,
  writeFailureMessage,
  type FailureMessages,
} from "@/lib/admin-write";
import { apiGet } from "@/lib/api";
import { DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type {
  MailDestination,
  MailDetail,
  MailListResponse,
  MailListRow,
  MailPurpose,
  MailStatus,
  MailUpload,
} from "@/types-mail";

// --- reads ------------------------------------------------------------------

export const MAIL_PAGE_SIZE = 50;

export interface MailListParams {
  page: number;
  status: string;
  purpose: string;
  companyId: string;
  search: string;
}

export function mailListQuery(params: MailListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "mail",
      "list",
      params.page,
      params.status,
      params.purpose,
      params.companyId,
      params.search,
    ] as const,
    queryFn: () =>
      apiGet<MailListResponse>("/api/admin/mail", {
        page: params.page,
        pageSize: MAIL_PAGE_SIZE,
        status: params.status,
        purpose: params.purpose,
        company_id: params.companyId,
        search: params.search,
      }),
  };
}

export function mailDetailQuery(id: string) {
  return {
    ...DETAIL,
    queryKey: ["mail", "detail", id] as const,
    queryFn: () =>
      apiGet<MailDetail>(`/api/admin/mail/${encodeURIComponent(id)}`),
  };
}

/** The registered IRS mailing addresses: a fixed registry, so it is read once per session. */
export function mailDestinationsQuery() {
  return {
    staleTime: Infinity,
    queryKey: ["mail", "destinations"] as const,
    queryFn: () => apiGet<MailDestination[]>("/api/admin/mail/destinations"),
  };
}

/**
 * The detail with every list and object present, so a partial answer (an older API, a test's empty
 * read) renders what it has instead of throwing on `actions.cancel` or `events.map`.
 */
export function withDetailDefaults(raw: Partial<MailDetail>): MailDetail {
  return {
    ...(raw as MailDetail),
    recipient: {
      lines: raw.recipient?.lines ?? [],
      contact: raw.recipient?.contact ?? null,
    },
    sender: {
      lines: raw.sender?.lines ?? [],
      contact: raw.sender?.contact ?? null,
    },
    events: Array.isArray(raw.events) ? raw.events : [],
    actions: {
      cancel: raw.actions?.cancel === true,
      progress: raw.actions?.progress === true,
    },
    mediaUrl: raw.mediaUrl ?? null,
    proofUrl: raw.proofUrl ?? null,
  };
}

// --- words ------------------------------------------------------------------

export const MAIL_PURPOSE_LABELS: Record<MailPurpose, string> = {
  name_correction: "Name correction",
  federal_tax_packet: "Tax return packet",
  form_7004: "Form 7004",
  staff_mailing: "Staff mailing",
};

export function mailPurposeLabel(purpose: string | null | undefined): string {
  if (!purpose) return "Letter";
  return (
    (MAIL_PURPOSE_LABELS as Record<string, string>)[purpose] ??
    purpose.replace(/_/g, " ")
  );
}

/** Still moving at PostGrid or USPS: the statuses a person may be waiting on. */
const IN_FLIGHT: readonly MailStatus[] = [
  "queued",
  "submitted",
  "printing",
  "mailed",
];

export function isMailInFlight(status: MailStatus): boolean {
  return IN_FLIGHT.includes(status);
}

/** The record the letter belongs to, as a console link, or null when the company is the subject. */
export function mailSubjectLink(
  row: Pick<MailListRow, "purpose" | "subjectId" | "orderId">,
): { to: string; label: string } | null {
  switch (row.purpose) {
    case "name_correction":
      return {
        to: `/ein-corrections/${encodeURIComponent(row.subjectId)}`,
        label: "Open the name correction",
      };
    case "federal_tax_packet":
      return row.orderId
        ? {
            to: `/federal-tax/${encodeURIComponent(row.orderId)}`,
            label: "Open the federal tax filing",
          }
        : null;
    case "form_7004":
      return {
        to: `/federal-tax/form-7004/${encodeURIComponent(row.orderId ?? row.subjectId)}`,
        label: "Open the Form 7004",
      };
    default:
      return null;
  }
}

/** USPS's own tracking page for a certified mail number. */
export function uspsTrackingUrl(trackingNumber: string): string {
  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(
    trackingNumber.replace(/\s+/g, ""),
  )}`;
}

/** What a status-trail entry's source means to staff. */
const SOURCE_LABELS: Record<string, string> = {
  send: "Sent to PostGrid",
  webhook: "PostGrid update",
  reconcile: "Checked with PostGrid",
  admin_cancel: "Cancelled by staff",
  admin_progress: "Advanced by staff (test)",
};

export function mailEventSourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source.replace(/_/g, " ");
}

// --- the next step ----------------------------------------------------------

export interface MailNextStep {
  /** Whose move it is, for the chip beside the headline. */
  owner: "team" | "waiting" | "none";
  headline: string;
  explanation: string;
}

const SEND_AGAIN =
  "send it again with New mailing on the Mail page, or from the record it belongs to";

/** Where a letter stands and what, if anything, staff do next. A pure reading of the API's row. */
export function mailNextStep(detail: MailDetail): MailNextStep {
  switch (detail.status) {
    case "queued":
      return {
        owner: "waiting",
        headline: "Queued to send",
        explanation:
          "The letter is waiting to go to PostGrid and usually moves on within a few minutes. If it is still queued after 30 minutes, the stuck-mail sweep sends it again on its own.",
      };
    case "submitted":
      return {
        owner: "waiting",
        headline: "At PostGrid, not printed yet",
        explanation: detail.actions.cancel
          ? "PostGrid has the letter and has not started printing. You can still cancel it here."
          : "PostGrid has the letter and has not started printing.",
      };
    case "printing":
      return {
        owner: "waiting",
        headline: "Printing",
        explanation:
          "PostGrid is printing and posting the letter. It can no longer be cancelled.",
      };
    case "mailed":
      return {
        owner: "waiting",
        headline: "Mailed: with USPS",
        explanation:
          "USPS has the letter. Follow it with the certified tracking number below; the mail proof is stored with the company's documents.",
      };
    case "delivered":
      return {
        owner: "none",
        headline: "Delivered",
        explanation: "USPS delivered the letter. Nothing more to do.",
      };
    case "returned":
      return {
        owner: "team",
        headline: "Returned to sender",
        explanation: `USPS returned the letter to our mailroom. Check the letter and the address, then ${SEND_AGAIN}.`,
      };
    case "cancelled":
      return {
        owner: "none",
        headline: "Cancelled",
        explanation: detail.cancelReason
          ? `Reason: ${detail.cancelReason}`
          : "This letter was cancelled before it was printed.",
      };
    case "failed":
      return {
        owner: "team",
        headline: "The letter was not sent",
        explanation: `${detail.errorMessage ? `${detail.errorMessage.replace(/\.$/, "")}. ` : ""}Fix the letter or the company record, then ${SEND_AGAIN}.`,
      };
    case "unconfirmed":
      return {
        owner: "team",
        headline: "Check PostGrid before sending again",
        explanation:
          "Our send got no clear answer from PostGrid. The stuck-mail sweep re-reads it every few hours. Look for this mailing id in the PostGrid dashboard before you send the letter again, so the IRS does not get it twice.",
      };
    default:
      return {
        owner: "none",
        headline: "Unknown status",
        explanation: "Reload the page. If it stays unknown, check Operations.",
      };
  }
}

// --- the staff mail tool ----------------------------------------------------

/** The letter ceiling the API and the Worker hold it to. */
export const MAIL_LETTER_MAX_BYTES = 10 * 1024 * 1024;

/** Why a chosen file cannot be mailed, or null. */
export function letterFileProblem(file: File | null): string | null {
  if (!file) return null;
  const pdf =
    file.type === "application/pdf" ||
    file.type === "application/x-pdf" ||
    (file.type === "" && file.name.toLowerCase().endsWith(".pdf"));
  if (!pdf)
    return "The letter must be a PDF. Export it as PDF and choose it again.";
  if (file.size === 0 || file.size > MAIL_LETTER_MAX_BYTES) {
    return "The letter must be between 1 byte and 10 MB. Choose a smaller PDF.";
  }
  return null;
}

/** The review step's sentence: what is mailed, to whom, and that it cannot be called back. */
export function staffMailingConfirmText(input: {
  companyName: string;
  fileName: string;
  destinationLabel: string;
}): string {
  return `This sends "${input.fileName}" for ${input.companyName} to ${input.destinationLabel} by USPS Certified Mail with an electronic return receipt. Once PostGrid starts printing it cannot be cancelled.`;
}

// --- writes -----------------------------------------------------------------

const MAIL_WRITE_HEADER = "X-Admin-Mail-Write";

const hasData =
  <T>(check: (data: T) => boolean) =>
  (result: { data?: T } | null): boolean =>
    result?.data !== undefined && check(result.data);

/** Step one of the staff tool: store the PDF under the company's keys. */
export async function uploadMailLetter(
  companyId: string,
  file: File,
): Promise<MailUpload> {
  const form = new FormData();
  form.append("companyId", companyId);
  // `letterFileProblem` takes a `.pdf` the browser gave no type; the Worker accepts only a typed
  // PDF part (the API checks the bytes), so it goes as one.
  const pdf = file.type
    ? file
    : new File([file], file.name, { type: "application/pdf" });
  form.append("file", pdf, file.name);
  const result = await adminPost<MailUpload>(
    "/api/admin/mail/uploads",
    MAIL_WRITE_HEADER,
    form,
    hasData<MailUpload>((data) => typeof data.mediaPath === "string"),
  );
  return result?.data as MailUpload;
}

/** Step two: mail the stored letter to a registered IRS destination. */
export async function createStaffMailing(input: {
  companyId: string;
  destinationId: string;
  mediaPath: string;
}): Promise<MailListRow> {
  const result = await adminPost<MailListRow>(
    "/api/admin/mail",
    MAIL_WRITE_HEADER,
    input,
    hasData<MailListRow>((data) => typeof data.id === "string"),
  );
  return result?.data as MailListRow;
}

const base = (id: string) => `/api/admin/mail/${encodeURIComponent(id)}`;

export function cancelMailing(id: string, note?: string) {
  const trimmed = note?.trim();
  return adminPost(
    `${base(id)}/cancel`,
    MAIL_WRITE_HEADER,
    trimmed ? { note: trimmed } : {},
  );
}

export function progressTestMailing(id: string) {
  return adminPost(`${base(id)}/progress`, MAIL_WRITE_HEADER, {});
}

const MAIL_FAILURES: FailureMessages = {
  ...BASE_FAILURE_MESSAGES,
  403: (said) =>
    `${said ?? "The console refused this write."} Reload the page and retry from this screen.`,
  404: (said) =>
    `${said ?? "Not found."} Reload the Mail page and pick it again.`,
  409: (said) =>
    `${said ?? "This mailing moved on."} Reload the page to see where it stands now.`,
  413: (said) =>
    `${said ?? "The file is too large."} Choose a smaller PDF and try again.`,
  502: (said) =>
    `${said ?? "PostGrid did not answer."} Wait a minute, then reload the page and try again.`,
  503: "The API did not answer. Reload the page to see whether the change landed before you try again.",
  504: "The API did not answer. Reload the page to see whether the change landed before you try again.",
  default: (said) =>
    `${said ?? "The action failed."} Fix what it says and try again, or reload the page.`,
};

/** What staff read when a mail write fails: the reason, and what to do next. */
export function mailWriteFailureMessage(error: unknown): string {
  return writeFailureMessage(error, MAIL_FAILURES);
}
